/**
 * Shared toast helper for `/prior-auth-utilities/*` (and similar) endpoints
 * that follow the "real HTTP status + `body.detail` on failure" contract.
 *
 * - 4xx with a `detail` payload  → toast the server-supplied user-actionable copy
 * - 5xx, network failures, parse failures, missing detail → toast the caller's
 *   per-flow generic fallback string
 *
 * Errors thrown by the matching client fns are plain `Error` objects with
 * `status` and `detail` attached as own properties.
 *
 * @module showApiError
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";

/** Plain `Error` enriched with the originating HTTP status and body detail. */
export interface ApiErrorLike extends Error {
  status?: number;
  detail?: string;
}

/**
 * Surfaces an API failure as a toast, branching on HTTP status.
 *
 * @param error - The thrown error from a client fn (expected to carry
 *   `status` + `detail`; safe to call with anything else).
 * @param fallback - Generic per-flow copy shown for 5xx / non-HTTP failures.
 */
export const showApiError = (error: unknown, fallback: string): void => {
  const err = error as ApiErrorLike;
  if (err?.status && err.status >= 400 && err.status < 500 && err.detail) {
    toast.error(err.detail);
    return;
  }
  toast.error(fallback);
};
